import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { API } from "../config/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(t('auth.resetSuccess'));
    } catch (error) {
      toast.error(t('auth.resetError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <div className="bg-white rounded-lg shadow-lg">
          <LanguageSwitcher />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-1 mb-4">
            <span className="text-4xl font-bold text-gray-800">Lo</span>
            <span className="text-4xl font-bold text-lojack-red">/</span>
            <span className="text-4xl font-bold text-gray-800">Jack</span>
          </div>
          <h2 className="text-xl text-gray-600">{t('auth.resetPassword')}</h2>
        </div>

        {sent ? (
          <div className="text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-600" size={32} />
              </div>
              <p className="text-gray-600">{t('auth.resetSuccess')}</p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition"
            >
              {t('auth.backToLogin')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <p className="text-gray-600 text-sm text-center mb-4">
              {t('auth.forgotPasswordHint')}
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent transition"
                placeholder="email@lojack.it"
                required
                data-testid="forgot-email"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-lojack-red text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="forgot-submit"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                t('auth.sendResetLink')
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full text-gray-600 py-2 hover:text-lojack-red transition text-sm"
            >
              &larr; {t('auth.backToLogin')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
